import {
    type JSX,
    useCallback,
    useEffect,
    useRef,
    useState } from 'react';

import { Box,Stack, TextField } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import { setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { APIClaimPostParams, APIClaimPutParams } from 'common/services/api/Claims/Claims.api';
import { postClaim, PostClaimThunkResponse, putClaim, PutClaimThunkResponse } from 'common/services/api/Claims/Claims.thunk';
import {
    EditDrawerContentProps,
    Operations
} from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { DEFAULT_FIELD_STYLE, editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';

import { ClaimDataTypesDropDown } from '../claimDataTypes/ClaimDataTypesDropDown';

interface ClaimItem {
    name: string;
    claimDataTypeName: string;
}

interface ClaimDrawerProperties {
    claimNameFieldRef: React.RefObject<HTMLInputElement | null>;
    claimTypeFieldRef: React.RefObject<HTMLInputElement | null>;
    tenantName: string;
    tenantId: string;
}

function useClaimDrawer(
    entityName: string,
    drawerProps: ClaimDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<ClaimItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();
    const [currentClaimTypeId, setCurrentClaimTypeId] = useState<string>();

    const drawerContext = useDrawerContext<
        ClaimItem,
        ClaimDrawerProperties,
        APIClaimPostParams,
        PostClaimThunkResponse,
        APIClaimPutParams,
        PutClaimThunkResponse
    >(entityName, drawerProps, editingItemInfo);

    const handleInput = useCallback(() => {
        dispatch(setSaveButtonEnabled(
            !!drawerProps.claimNameFieldRef?.current?.value &&
            !!currentClaimTypeId));
    }, [dispatch, drawerProps.claimNameFieldRef, currentClaimTypeId]);

    const handleSaveBtnEnabled = useCallback(() => {
        dispatch(setSaveButtonEnabled(!!drawerProps.claimNameFieldRef?.current?.value));
    }, [dispatch, drawerProps.claimNameFieldRef]);

    useEffect(() => {
        handleInput();
    }, [currentClaimTypeId, handleInput]);

    useEffect(() => {
        if (drawerContext.currentId === DrawerIDs.Claims) {
            setCurrentClaimTypeId('');
        }
    }, [drawerContext.isOpenDrawer, drawerContext.currentId]);

    const getPostOptions = useCallback((): APIClaimPostParams => ({
        name: drawerProps.claimNameFieldRef.current?.value ?? '',
        tenantId: drawerProps.tenantId,
        claimDataTypeId: currentClaimTypeId ?? ''
    }), [drawerProps, currentClaimTypeId]);

    const getPutOptions = useCallback((): APIClaimPutParams => ({
        name: drawerProps.claimNameFieldRef.current?.value ?? '',
        id: editingItemInfo?.editingItemId ?? '',
        tenantId: drawerProps.tenantId
    }), [drawerProps, editingItemInfo?.editingItemId]);

    const createSuccessHandler = useCallback((postResult: PostClaimThunkResponse) => {
        drawerContext.createSuccessHandler(postResult);
        dispatch(setRows(postResult.rows));
    }, [drawerContext, dispatch]);

    const editSuccessHandler = useCallback((putResult: PutClaimThunkResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setRows(putResult.rows));
    }, [drawerContext, dispatch]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postClaim, getPostOptions, createSuccessHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putClaim, getPutOptions, editSuccessHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler]);

    return {
        ...drawerContext,
        currentClaimTypeId,
        setCurrentClaimTypeId,
        handleInput,
        handleSaveBtnEnabled,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    handleInput,
    setCurrentClaimTypeId,
    postCallback,
    drawerProps
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    handleInput: () => void;
    setCurrentClaimTypeId: (dataTypeId: string) => void;
    postCallback: () => void;
    drawerProps: ClaimDrawerProperties;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps.claimNameFieldRef}
                    id="filled-basic"
                    size="small"
                    label="Claim Name"
                    placeholder="Type Claim Name"
                    variant="filled"
                    focused
                    autoFocus={true}
                    onChange={handleInput}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                />
                {nameInConflict &&
                <Box component='div'
                    display='inline'
                    sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    Claim name already in use
                </Box>
                }
                <ClaimDataTypesDropDown
                    inputRef={drawerProps.claimTypeFieldRef}
                    onChange={setCurrentClaimTypeId}
                    defaultTypeName='String' />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={drawerProps.tenantName}
                    disabled
                />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    nameInConflict,
    handleSaveBtnEnabled,
    putCallback,
    drawerProps,
    editingItemInfo
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    handleSaveBtnEnabled: () => void;
    putCallback: () => void;
    drawerProps: ClaimDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<ClaimItem>['editingItemInfo'];
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps.claimNameFieldRef}
                    defaultValue={editingItemInfo?.editingRow?.name}
                    id="filled-basic"
                    size="small"
                    label="Claim Name"
                    placeholder="Type Claim Name"
                    variant="filled"
                    focused
                    autoFocus={true}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                />
                {nameInConflict &&
                <Box component='div'
                    display='inline'
                    sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    Claim name already in use
                </Box>
                }
                <ClaimDataTypesDropDown
                    inputRef={drawerProps.claimTypeFieldRef}
                    defaultTypeName={editingItemInfo?.editingRow?.claimDataTypeName ?? 'String'}
                    disabled={true}
                />
                <TextField
                    sx={DEFAULT_FIELD_STYLE}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={drawerProps.tenantName}
                    disabled
                />
            </Stack>
        </EditDrawer>
    );
}

export function ClaimCreateContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<ClaimItem>>) {
    const tenantDetails = useAppSelector(selectPageInfo);
    const claimDrawerProps: ClaimDrawerProperties = {
        claimNameFieldRef: useRef<HTMLInputElement>(null),
        claimTypeFieldRef: useRef<HTMLInputElement>(null),
        tenantId: tenantDetails?.id ?? '',
        tenantName: tenantDetails?.name ?? ''
    };

    const drawerState = useClaimDrawer(title, claimDrawerProps, editingItemInfo);

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                handleInput={drawerState.handleInput}
                setCurrentClaimTypeId={drawerState.setCurrentClaimTypeId}
                postCallback={drawerState.postCallback}
                drawerProps={claimDrawerProps}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                handleSaveBtnEnabled={drawerState.handleSaveBtnEnabled}
                putCallback={drawerState.putCallback}
                drawerProps={claimDrawerProps}
                editingItemInfo={editingItemInfo}
            />
        );
    }

    return <></>;
}
